import { type ReactNode, useId } from 'react';
import { cx } from '../cx';
import { Icon } from '../Icon/Icon';

/**
 * THE KIT'S FORMS GROUP BOX: a wash-filled, 12-radius box of label-left,
 * control-right rows with hairlines between them, and a bold title on the pane
 * ABOVE it rather than inside it.
 *
 * This is the construction System Settings uses for every details list and the one
 * `spec/screens/record.md` section 2 item 6 puts in the record's inspector pane. It
 * replaces the plain two-column grid the record page drew before, which had no kit
 * master at all (`spec/screens/record.md` Open: the last HYPOTHESIS of that file).
 *
 * CONTENT LAYER, THEREFORE OPAQUE (DESIGN.md surface language). The box is a wash
 * over the window background, not a card: no border, no shadow, no glass. A box
 * inside a box is what the HIG's layout rule forbids, so nothing here draws a second
 * frame around a row.
 *
 * A SECTION CAN BE A DISCLOSURE, and it is the same component either way: pass
 * `open` and `onOpenChange` and the title becomes the trigger with a chevron in
 * front of it (the record's inspector opens the first section and folds the rest).
 * Without them the title is a plain heading and the rows always show.
 */
export interface GroupedFormProps {
  /** The bold section title, on the pane above the box. Omit for an untitled group. */
  readonly title?: string | undefined;
  /** Rendered at the trailing end of the title line, OUTSIDE the disclosure trigger. */
  readonly actions?: ReactNode | undefined;
  /** The grey help line under the box, the way a settings group explains itself. */
  readonly footer?: ReactNode | undefined;
  /** Present together with `onOpenChange` makes the title a disclosure. Controlled. */
  readonly open?: boolean | undefined;
  readonly onOpenChange?: ((open: boolean) => void) | undefined;
  readonly children: ReactNode;
  readonly className?: string | undefined;
}

export function GroupedForm({
  title,
  actions,
  footer,
  open,
  onOpenChange,
  children,
  className,
}: GroupedFormProps) {
  const bodyId = useId();
  const titleId = useId();
  const collapsible = title !== undefined && open !== undefined && onOpenChange !== undefined;
  const shown = !collapsible || open === true;
  return (
    /**
     * A TITLED GROUP IS A NAMED REGION (wave 3, R-B1). A `<section>` with no accessible name
     * is a generic box, so a pane of eight titled groups was eight things a screen reader had
     * to walk linearly - which is why the record's own sections used to wrap themselves in a
     * second `<section aria-label>` to get the landmark back. The title is the name a sighted
     * reader scans for, so it is the name this region carries too, and the wrapper is gone.
     * hig: https://developer.apple.com/design/human-interface-guidelines/accessibility
     */
    <section
      className={cx('record-form', className)}
      {...(title === undefined ? {} : { 'aria-labelledby': titleId })}
    >
      {(title !== undefined || actions !== undefined) && (
        <div className="record-form-header">
          {collapsible ? (
            <button
              type="button"
              aria-expanded={open}
              aria-controls={bodyId}
              onClick={() => onOpenChange(!(open ?? false))}
              className="record-form-trigger pressable"
            >
              <Icon
                name="chevron-right"
                size="0.9rem"
                className={cx(
                  'shrink-0 text-ink-subtle transition-transform duration-(--duration-fast)',
                  open === true && 'rotate-90',
                )}
              />
              <span id={titleId} className="record-form-title">
                {title}
              </span>
            </button>
          ) : (
            title !== undefined && (
              <h3 id={titleId} className="record-form-title">
                {title}
              </h3>
            )
          )}
          {actions}
        </div>
      )}
      {/* `hidden` rather than unmounting: a closed section keeps its rows out of the
          tab order (D7's "tab order never enters a collapsed section") without losing
          the editor state of a row somebody folded away mid-edit. */}
      <div id={bodyId} hidden={!shown} className="record-group">
        {children}
      </div>
      {footer !== undefined && shown && <p className="record-form-footer">{footer}</p>}
    </section>
  );
}

/**
 * One row of the group box: the label on the leading side, the value or control on
 * the trailing side, at the kit's ordinary row height.
 *
 * `control` takes the kit's taller control row (a row whose trailing side holds a
 * real control rather than a value). `stacked` puts the value UNDER the label, which
 * is what a long value needs and what the row does below the desktop breakpoint
 * anyway - a 120 label column beside a shipping address on a phone is two words per
 * line.
 */
export interface GroupedFormRowProps {
  readonly label: ReactNode;
  readonly children: ReactNode;
  readonly control?: boolean | undefined;
  readonly stacked?: boolean | undefined;
  /** A line under the value: a validation message, a provenance note, a hint. */
  readonly note?: ReactNode | undefined;
  readonly className?: string | undefined;
}

export function GroupedFormRow({
  label,
  children,
  control = false,
  stacked = false,
  note,
  className,
}: GroupedFormRowProps) {
  return (
    <div
      className={cx(
        'record-row',
        control && 'record-row-control',
        stacked && 'record-row-stacked',
        className,
      )}
    >
      <span className="record-row-label">{label}</span>
      <div className="record-row-value">
        {children}
        {note}
      </div>
    </div>
  );
}
